import { Injectable, OnDestroy } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse
  } from '@angular/common/http';
import { Subject, ReplaySubject, Observable, of, BehaviorSubject, zip } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { retry, takeUntil, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Company } from 'app/models';
import { UsersService } from 'app/services/users-service/users.service';
import { BranchUsers } from 'app/models/branchUsers';
import { ApiUrl } from 'app/constants/ApiUrls';
import { branchAlphaNumericSort } from 'app/helpers/branchAlphaNumericSortHelper';

@Injectable()
export class CompanyService implements OnDestroy {
  readonly companyPhotoUrl = `${environment.apiUrl}/api/CompanyPhoto`;

  private _company: BehaviorSubject<Company> = new BehaviorSubject<Company>(null);
  public company$: Observable<any> = this._company.asObservable();
  private readonly _branchesUsers: BehaviorSubject<BranchUsers[]> = new BehaviorSubject<BranchUsers[]>(null);
  public branchesUsers$: Observable<BranchUsers[]> = this._branchesUsers.asObservable();
  private _error: ReplaySubject<string> = new ReplaySubject<string>();
  public error$: Observable<string> = this._error.asObservable();
  private onDestroy$ = new Subject<void>();

  private _companies: BehaviorSubject<Company[]> = new BehaviorSubject<
    Company[]
  >(null);
  public companies$: Observable<any> = this._companies.asObservable();

  public companySearchString = '';
  public users: any = [];
  public loading: boolean;

  constructor(
    private _http: HttpClient,
    private _usersService: UsersService,
    private _translateService: TranslateService,
    public snackBar: MatSnackBar
  ) {}

  getCompany(id: string, isUserCountNotReq?: boolean) {
    this.loading = true;
    this._http
      .get(`${ApiUrl.companies}/${id}`)
      .pipe(catchError(error => this._handleError(error)))
      .subscribe((data: any) => {
        if (data.errorMessage) {
          this._error.next(data.errorMessage);
          this.loading = false;
        } else {
          this._handleData(data);
          this.loading = false;
        }
      });
  }

  getCompanyBranchesWithUsers(companyId: string) {
    let companyUrl = ApiUrl.companies
    this._http.get(`${companyUrl}/${companyId}/Branches/Users`).pipe(catchError(error => this._handleError(error))).subscribe((data: any) => {
      if (data.errorMessage) {
        this._error.next(data.errorMessage);
      } else {
        this._branchesUsers.next(data);
      }
    });
  }

  private _handleData(data, isUserCountNotReq?: boolean): void {
    this._error.next(null);
    const company = data as Company;
    const companyAsBranch = { ...company };
    delete companyAsBranch.branches;
    // company chosen internally won't have a branches property
    if (company.branches) {
      company.branches.sort(branchAlphaNumericSort)
      company.branches.unshift(companyAsBranch);
    } else {
      company.branches = [];
    }
    this._company.next(company);
    if (!isUserCountNotReq) {
      this.getUserCounts(company);
    }
  }

  saveCompanyImage(image: string | ArrayBuffer, accountId: string): void {
    let savingMsg: string;
    this._translateService
      .get('USERS_SERVICE.saving')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (savingMsg = value));
    this._http
      .put(`${this.companyPhotoUrl}/${accountId}`, { image })
      .pipe(retry(2))
      .subscribe(
        data => {
          let successMsg: string;
          this._translateService
            .get('USERS_SERVICE.success')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(value => (successMsg = value));
          this.snackBar.dismiss();
          this.snackBar.open(successMsg, null, { duration: 2000 });
        },
        error => this.handleUpdatePhotoError(error)
      );
  }

  private handleUpdatePhotoError(error: HttpErrorResponse): void {
    let savingErrorMsg: string;
    this._translateService
      .get('ERROR_SAVING')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (savingErrorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[company-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[company-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }

    this._error.next(savingErrorMsg);
  }

  getUserCounts(company: Company): void {
    const branchesUsers$ = this.branchesUsers$;
    const companyUsers$ = this._usersService.companyUsers$;
  
    zip([branchesUsers$, companyUsers$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: ([branchesUsers, companyUsers]) => {
          if (companyUsers && branchesUsers) {
            this.users = companyUsers;
            company.employeeCount = companyUsers.length;
  
            const activeUserIds = new Set(companyUsers.map(user => user.id));
  
            company.branches.forEach(branch => {
              const branchData = branchesUsers.find(b => b.branchId === branch.accountId);
              if (branchData) {
                branch.employeeCount = branchData.users.filter(user => activeUserIds.has(user.userId)).length;
              } else {
                branch.employeeCount = 0;
              }
            });
            this._company.next(company);
          } else {
            this.users = [];
            company.employeeCount = 0;
          }
        },
        error: (error) => this._handleUsersError(error)
      });
  }

  private _handleError(error: HttpErrorResponse) {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(`[company-service] ERROR: ${error.error.message}`);
    } else {
      console.error(
        `[company-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    if(error.status == 404){
      return of([]);
    }
    return of({ errorMessage: errorMsg });
  }

  private _handleUsersError(error: HttpErrorResponse) {
    let errorMsg: string;
    this._translateService
      .get('ERROR_TRY_LATER')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(value => (errorMsg = value));
    if (error.error instanceof ErrorEvent) {
      console.error(
        `[company-service][users-service] ERROR: ${error.error.message}`
      );
    } else {
      console.error(
        `[company-service][users-service] STATUS: ${error.status}, ERROR: ${error.message}`
      );
    }
    // fail silently
  }

  setCompany(company: Company): void {
    this._setRecentCompanies(company);
    this._handleData(company);
  }

  private _setRecentCompanies(company: Company): void {
    let recentCompaniesList: Company[] = JSON.parse(
      localStorage.getItem('recentCompanies')
    );
    const companyInList = Boolean(
      recentCompaniesList &&
        recentCompaniesList.find(co => co.accountId === company.accountId)
    );
    if (!recentCompaniesList) {
      recentCompaniesList = [company];
      localStorage.setItem(
        'recentCompanies',
        JSON.stringify(recentCompaniesList)
      );
    } else if (companyInList) {
      // move co to top
      const coIndex = recentCompaniesList.findIndex(
        co => co.accountId === company.accountId
      );
      recentCompaniesList.splice(coIndex, 1);
      recentCompaniesList.unshift(company);
      localStorage.setItem(
        'recentCompanies',
        JSON.stringify(recentCompaniesList)
      );
    } else if (!companyInList) {
      recentCompaniesList.unshift(company);
      const trimmedCompanies = recentCompaniesList.slice(0, 5);
      localStorage.setItem('recentCompanies', JSON.stringify(trimmedCompanies));
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
